import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, getActivePropertyId, setActivePropertyId } from '../lib/api';
import { setNumberFormat } from '../lib/money';
import { settingsApi } from '../features/settings/settingsApi';
import { useAuth } from '../auth/AuthContext';

const PropertyContext = createContext(null);

/** Fallback ceiling used until the organization's configured limit has loaded. */
export const DEFAULT_MAX_UPLOAD_MB = 25;

export function PropertyProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activePropertyId, setActiveId] = useState(() => getActivePropertyId());

  const [perms, setPerms] = useState({ all: false, permissions: [], approvalLimitAmount: null, approvalLimitCurrency: null, roleIds: [], isSuperAdmin: false });
  const [permsLoaded, setPermsLoaded] = useState(false);

  // Effective commercial settings (currencies, default currency, number format) for the
  // active property scope — the single source of truth for currency dropdowns app-wide.
  const [commercial, setCommercial] = useState(null);

  const refreshCommercial = useCallback(async () => {
    try {
      const eff = await settingsApi.getEffective(getActivePropertyId());
      setCommercial(eff || null);
      setNumberFormat(eff);
      return eff;
    } catch {
      return null;
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await settingsApi.listProperties();
      const items = Array.isArray(list) ? list : list?.items ?? [];
      setProperties(items);
      setLoaded(true);
      return items;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshPermissions = useCallback(async () => {
    try {
      const res = await api.get('/me/permissions');
      setPerms({
        all: !!res?.all,
        permissions: res?.permissions ?? [],
        approvalLimitAmount: res?.approvalLimitAmount ?? null,
        approvalLimitCurrency: res?.approvalLimitCurrency ?? null,
        roleIds: res?.roleIds ?? [],
        isSuperAdmin: !!res?.isSuperAdmin,
      });
    } catch {
      setPerms({ all: false, permissions: [], approvalLimitAmount: null, approvalLimitCurrency: null, roleIds: [], isSuperAdmin: false });
    } finally {
      setPermsLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setProperties([]);
      setLoaded(false);
      setPermsLoaded(false);
      return;
    }
    reload().catch(() => setLoaded(true));
  }, [isAuthenticated, reload]);

  // Permissions depend on the active property context; refetch whenever it changes.
  useEffect(() => {
    if (!isAuthenticated) return;
    setPermsLoaded(false);
    refreshPermissions();
  }, [isAuthenticated, activePropertyId, refreshPermissions]);

  // Commercial settings (currency list + number format) also follow the active property.
  useEffect(() => {
    if (!isAuthenticated) {
      setCommercial(null);
      return;
    }
    refreshCommercial();
  }, [isAuthenticated, activePropertyId, refreshCommercial]);

  const setActiveProperty = useCallback((id) => {
    setActivePropertyId(id || null);
    setActiveId(id || null);
  }, []);

  const activeProperty = useMemo(
    () => properties.find((p) => p.id === activePropertyId) || null,
    [properties, activePropertyId],
  );

  const permissionSet = useMemo(() => new Set(perms.permissions), [perms.permissions]);
  const roleIdSet = useMemo(() => new Set(perms.roleIds), [perms.roleIds]);

  const hasPermission = useCallback(
    (key) => {
      if (!key) return true;
      if (!permsLoaded) return true; // optimistic until resolved, then settles
      return perms.all || permissionSet.has(key);
    },
    [permsLoaded, perms.all, permissionSet],
  );

  // Whether the user can act on an approval step requiring `roleId`. A blank roleId is a
  // generic fallback step (gated only by the approve permission). Super admins bypass.
  const canActOnStepRole = useCallback(
    (roleId) => {
      if (perms.all || perms.isSuperAdmin) return true;
      if (!roleId) return true;
      return roleIdSet.has(roleId);
    },
    [perms.all, perms.isSuperAdmin, roleIdSet],
  );

  const value = useMemo(
    () => ({
      properties,
      loading,
      loaded,
      reload,
      activePropertyId,
      activeProperty,
      setActiveProperty,
      // permissions
      permsLoaded,
      allPermissions: perms.all,
      permissions: perms.permissions,
      approvalLimit: perms.approvalLimitAmount != null
        ? { amount: perms.approvalLimitAmount, currency: perms.approvalLimitCurrency }
        : null,
      roleIds: perms.roleIds,
      hasPermission,
      canActOnStepRole,
      refreshPermissions,
      // commercial settings
      commercial,
      refreshCommercial,
    }),
    [properties, loading, loaded, reload, activePropertyId, activeProperty, setActiveProperty,
      permsLoaded, perms, hasPermission, canActOnStepRole, refreshPermissions,
      commercial, refreshCommercial],
  );

  return <PropertyContext.Provider value={value}>{children}</PropertyContext.Provider>;
}

export function useProperty() {
  const ctx = useContext(PropertyContext);
  if (!ctx) throw new Error('useProperty must be used within a PropertyProvider');
  return ctx;
}

export function usePermissions() {
  const { hasPermission, permsLoaded, allPermissions, permissions, approvalLimit } = useProperty();
  return { hasPermission, permsLoaded, all: allPermissions, permissions, approvalLimit };
}

/**
 * The effective commercial settings for the active property scope: the configured
 * currency list, default currency, and number format. Use this instead of hardcoding
 * currency lists in forms.
 */
export function useCommercialSettings() {
  const { commercial, refreshCommercial } = useProperty();
  return {
    commercial,
    refreshCommercial,
    currencies: commercial?.currencies?.length ? commercial.currencies : ['USD'],
    defaultCurrency: commercial?.defaultCurrency || commercial?.currencies?.[0] || 'USD',
  };
}

/**
 * The organization's upload ceiling. Carried on the commercial settings the app already loads,
 * so an upload control can reject a large file locally instead of sending it and failing.
 */
export function useMaxUploadBytes() {
  const { commercial } = useProperty();
  const mb = Number(commercial?.maxAttachmentSizeMb) || DEFAULT_MAX_UPLOAD_MB;
  return { maxMb: mb, maxBytes: mb * 1024 * 1024 };
}
